import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';

const SHARE_OPTIONS = [
  {
    value: 'private',
    label: 'Private',
    description: 'Only visible to you',
    Icon: LockOutlinedIcon,
  },
  {
    value: 'project',
    label: 'Project',
    description: 'All project members',
    Icon: FolderOutlinedIcon,
  },
  {
    value: 'organization',
    label: 'Organization',
    description: 'All org members',
    disabledDescription: 'Only org admins & managers can share org-wide',
    Icon: BusinessOutlinedIcon,
  },
];

const ORG_ALLOWED_ROLES = ['org-admin', 'org-manager'];

const SavePresetPopover = ({
  open = false,
  anchorEl = null,
  onClose,
  onSave,
  saving = false,
  error = null,
  userRole = 'member',
  mode = 'save',
  initialName = '',
  initialShareLevel = 'private',
}) => {
  const [intent, setIntent] = useState('update');
  const [name, setName] = useState('');
  const [shareLevel, setShareLevel] = useState('private');
  const inputRef = useRef(null);

  const canShareOrg = ORG_ALLOWED_ROLES.includes(userRole);
  const isUpdateMode = mode === 'update';
  const isOverwriting = isUpdateMode && intent === 'update';

useEffect(() => {
  if (!open) return;

  setIntent('update');
  setName('');
  setShareLevel(isUpdateMode ? initialShareLevel : 'private');

  if (!isUpdateMode) {
    setTimeout(() => inputRef.current?.focus(), 50);
  }
}, [open, initialShareLevel, isUpdateMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleIntentChange = (next) => {
    if (saving) return;
    setIntent(next);
    setName('');
    if (next === 'new') {
      setShareLevel('private');
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setShareLevel(initialShareLevel);
    }
  };

  const handleSubmit = async () => {
    if (saving) return;
    if (!isOverwriting && !name.trim()) return;
    const finalName = isOverwriting ? initialName : name.trim();
    await onSave?.(finalName, shareLevel, isOverwriting);
  };

  const handleClose = () => {
    setIntent('update');
    setName('');
    setShareLevel('private');
    onClose?.();
  };

  const headerLabel = isUpdateMode
    ? isOverwriting
      ? 'Update filter'
      : 'Save as new filter'
    : 'Save filter';

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      PaperProps={{
        elevation: 3,
        sx: { p: 2, width: 300, borderRadius: 2 },
      }}>
      <Stack spacing={2}>
        <Box display="flex" alignItems="center" gap={0.75}>
          <BookmarkAddIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" fontWeight={600}>
            {headerLabel}
          </Typography>
        </Box>

        {isUpdateMode && (
          <Box
            display="flex"
            sx={{
              bgcolor: 'action.hover',
              borderRadius: 1.5,
              p: 0.375,
              gap: 0.375,
            }}>
            {[
              { value: 'update', label: `Update "${initialName}"` },
              { value: 'new', label: 'Save as new' },
            ].map(({ value, label }) => {
              const selected = intent === value;
              return (
                <Box
                  key={value}
                  onClick={() => handleIntentChange(value)}
                  sx={{
                    flex: 1,
                    textAlign: 'center',
                    px: 1,
                    py: 0.625,
                    borderRadius: 1.25,
                    cursor: saving ? 'default' : 'pointer',
                    bgcolor: selected ? 'background.paper' : 'transparent',
                    boxShadow: selected ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
                    transition: 'background-color 0.15s, box-shadow 0.15s',
                    userSelect: 'none',
                  }}>
                  <Typography
                    variant="caption"
                    fontWeight={selected ? 600 : 400}
                    color={selected ? 'text.primary' : 'text.secondary'}
                    noWrap
                    sx={{ fontSize: '0.72rem', display: 'block' }}>
                    {label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {isOverwriting ? (
          <Box
            sx={{
              bgcolor: 'action.selected',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              px: 1.5,
              py: 1,
            }}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ lineHeight: 1.6 }}>
              The current filters will overwrite{' '}
              <Box component="span" fontWeight={600} color="text.primary">
                "{initialName}"
              </Box>
              . This cannot be undone.
            </Typography>
          </Box>
        ) : (
          <TextField
            inputRef={inputRef}
            fullWidth
            size="small"
            label="Filter name"
            placeholder="e.g. High priority open bugs"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmit();
              if (e.key === 'Escape') handleClose();
            }}
            disabled={saving}
            error={!!error}
            helperText={error ?? undefined}
          />
        )}

        {isOverwriting && error && (
          <Typography variant="caption" color="error.main" sx={{ mt: -1 }}>
            {error}
          </Typography>
        )}

        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={500}
            mb={0.75}
            display="block">
            Visibility
          </Typography>
          <Stack spacing={0.625}>
            {SHARE_OPTIONS.map(({ value, label, description, disabledDescription, Icon }) => {
              const isOrgOption = value === 'organization';
              const isDisabled = isOrgOption && !canShareOrg;
              const isSelected = shareLevel === value;

              const card = (
                <Box
                  onClick={() => !isDisabled && !saving && setShareLevel(value)}
                  sx={{
                    border: '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    borderRadius: 1.5,
                    px: 1.25,
                    py: 0.875,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: isDisabled || saving ? 'default' : 'pointer',
                    opacity: isDisabled ? 0.45 : 1,
                    bgcolor: isSelected
                      ? (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(99,102,241,0.12)'
                            : 'rgba(99,102,241,0.07)'
                      : 'transparent',
                    transition: 'border-color 0.15s, background-color 0.15s',
                    '&:hover':
                      !isDisabled && !saving && !isSelected
                        ? { borderColor: 'text.disabled', bgcolor: 'action.hover' }
                        : {},
                  }}>
                  <Icon
                    sx={{
                      fontSize: 14,
                      flexShrink: 0,
                      color: isSelected ? 'primary.main' : 'text.disabled',
                    }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      lineHeight={1.3}
                      color={isSelected ? 'primary.main' : 'text.primary'}
                      display="block">
                      {label}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      lineHeight={1.3}
                      sx={{ fontSize: '0.67rem' }}>
                      {description}
                    </Typography>
                  </Box>
                </Box>
              );

              return isDisabled ? (
                <Tooltip key={value} title={disabledDescription ?? ''} placement="bottom" arrow>
                  <span>{card}</span>
                </Tooltip>
              ) : (
                <Box key={value}>{card}</Box>
              );
            })}
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="text"
            size="small"
            onClick={handleClose}
            disabled={saving}
            sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={(!isOverwriting && !name.trim()) || saving}
            startIcon={saving ? <CircularProgress size={13} color="inherit" /> : null}>
            {isOverwriting ? 'Update' : 'Save'}
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
};

export default SavePresetPopover;